import React, { FC } from 'react';
import { useStaticPermission } from 'common/auth/Authorize';
import { DataTableRowActions, DataTableRowAction } from '@utmost.co/utmost-design-system';
import { tokens } from '@beeline.com/bp-design-system';
import { CustomFieldDefinitionEntityTypeModel, StatusModel } from 'src/graphql/types';
import { useTypedMutation } from 'src/utils/hooks/useTypedMutation';
import { CustomFieldDefinition } from '../../CustomFieldDefinitions/utils/CustomFields.types';
import { DataTableObject } from '../utils/Objects.types';
import { UpdateObjectStatusDocument } from '../utils/__generated__/UpdateObjectStatus.graphql';
import { DeleteObjectDefinitionDocument } from '../utils/__generated__/DeleteObject.graphql';
import { useCustomFieldsAndObjectsIntl } from '../../translations/useCustomFieldsAndObjectsIntl';
import { IsFeatureOn } from 'common/featureFlag/IsFeatureOn';

interface Props {
  object: DataTableObject;
  onAdd: (customDefinition: WithOptionalUid<CustomFieldDefinition>) => void;
  onEdit: (customDefinition: WithOptionalUid<CustomFieldDefinition>) => void;
  onSubmitDone: () => void;
  onEditCustomObjectValues: (customObjectDefinitionUid: string, customObjectDefinitionName: string) => void;
}

export const ObjectActions: FC<React.PropsWithChildren<Props>> = ({
  object,
  onAdd,
  onEdit,
  onSubmitDone,
  onEditCustomObjectValues
}) => {
  const intl = useCustomFieldsAndObjectsIntl();
  const { DELETE, UPDATE, CREATE } = useStaticPermission('CUSTOM_FIELD_DEFINITION');
  const { subRows, parentId, ...restOfObject } = object;
  const isParent = object.parentId === object.uid;

  const { set: setObjectStatus } = useTypedMutation(UpdateObjectStatusDocument, {
    variables: {
      customFieldDefinition: {
        ...restOfObject,
        status: object.status === StatusModel.Active ? StatusModel.Inactive : StatusModel.Active
      }
    }
  });

  const { set: deleteObject } = useTypedMutation(DeleteObjectDefinitionDocument, {
    variables: {
      id: object.uid
    }
  });

  const onDeleteHandler = async () => {
    await deleteObject();
    onSubmitDone();
  };

  const onStatusChangeHandler = async () => {
    await setObjectStatus();
    onSubmitDone();
  };

  const onEditHandler = () => {
    onEdit(restOfObject as WithOptionalUid<CustomFieldDefinition>);
  };

  const onEditCustomObjectHandler = () => {
    if (object.uid && object.name) {
      onEditCustomObjectValues(object.uid, object.name);
    }
  };

  const onAddHandler = () => {
    onAdd({
      parentEntityType: CustomFieldDefinitionEntityTypeModel.CustomObject,
      parentCustomObjectDefinition: { uid: object.uid, name: object.name }
    });
  };

  if (object.external) {
    return null;
  }

  return isParent ? (
    <DataTableRowActions>
      <DataTableRowAction
        dataTest="add-child-object"
        onClick={onAddHandler}
        disabled={!CREATE}
        disabledMessage={intl.noPermissionForAction}
      >
        {intl.addCustomObjectFor({ name: object.name })}
      </DataTableRowAction>
      <IsFeatureOn featureId="custom-object-values">
        <DataTableRowAction dataTest="edit-custom-object-values" onClick={onEditCustomObjectHandler}>
          {intl.editCustomObjectValuesFor({ name: object.name })}
        </DataTableRowAction>
      </IsFeatureOn>
    </DataTableRowActions>
  ) : (
    <DataTableRowActions>
      <DataTableRowAction
        dataTest="edit-custom-object"
        disabled={!UPDATE}
        disabledMessage={intl.noPermissionForAction}
        onClick={onEditHandler}
      >
        {intl.edit}
      </DataTableRowAction>
      <DataTableRowAction
        dataTest="delete-custom-object"
        disabled={!DELETE}
        disabledMessage={intl.noPermissionForAction}
        style={{ color: tokens.colorAlert }}
        onClick={() => onDeleteHandler()}
      >
        {intl.delete}
      </DataTableRowAction>
      {object.status === StatusModel.Active ? (
        <DataTableRowAction
          dataTest="deactivate-custom-object"
          disabled={!UPDATE}
          disabledMessage={intl.noPermissionForAction}
          onClick={() => onStatusChangeHandler()}
        >
          {intl.deactivate}
        </DataTableRowAction>
      ) : (
        <DataTableRowAction
          dataTest="activate-custom-object"
          disabled={!UPDATE}
          disabledMessage={intl.noPermissionForAction}
          onClick={() => onStatusChangeHandler()}
        >
          {intl.activate}
        </DataTableRowAction>
      )}
    </DataTableRowActions>
  );
};
